import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { PrinterOptionsSchema } from "../printnode/printer";

export enum PrintableDocument {
  PICK_LIST = "pickList",
  PACKING_SLIP = "packingSlip",
  LABEL = "label",
  COMMERCIAL_INVOICE = "commercialInvoice",
  PERSONALIZED_SLIP = "personalizedSlip",
  RETURN_LABEL = "returnLabel",
  LOCAL_PICK_UP_LABEL = "localPickUpLabel",
  BILL_OF_LADING = "billOfLading",
  PARCEL_PACKING_SLIP = "parcelPackingSlip",
  ORDER_SUMMARY = "orderSummary",
}

export enum PrintNodePrintJobStatus {
  NEW = "new",
  SENT_TO_CLIENT = "sent_to_client",
  QUEUED = "queued",
  IN_PROGRESS = "in_progress",
  ERROR = "error",
  DONE = "done",
  CANCELLED = "cancelled",
  DELETED = "deleted",
}

export enum PrintJobStatus {
  CREATED = "created",
  PURCHASED = "purchased",
  GENERATING = "generating",
  GENERATED = "generated",
  INITIALIZING_PRINTING = "initializingPrinting",
  SENT_TO_PRINT_NODE = "sentToPrintNode",
  PRINT_NODE_CONFIRMED = "printNodeConfirmed",
  SENT_TO_PRINTER = "sentToPrinter",
  COMPLETED = "completed",
  BATCH_COMPLETED = "batchCompleted",
  FAILED = "failed",
  CANCELLING = "cancelling",
  CANCELLED = "cancelled",
}

export const TERMINAL_PRINT_JOB_STATUSES: ReadonlySet<PrintJobStatus> = new Set(
  [
    PrintJobStatus.COMPLETED,
    PrintJobStatus.BATCH_COMPLETED,
    PrintJobStatus.FAILED,
    PrintJobStatus.CANCELLED,
  ],
);

export const PRINT_JOB_STATUS_BY_PRINT_NODE_STATE: Record<
  PrintNodePrintJobStatus,
  PrintJobStatus
> = {
  [PrintNodePrintJobStatus.NEW]: PrintJobStatus.PRINT_NODE_CONFIRMED,
  [PrintNodePrintJobStatus.SENT_TO_CLIENT]: PrintJobStatus.SENT_TO_PRINTER,
  [PrintNodePrintJobStatus.QUEUED]: PrintJobStatus.SENT_TO_PRINTER,
  [PrintNodePrintJobStatus.IN_PROGRESS]: PrintJobStatus.SENT_TO_PRINTER,
  [PrintNodePrintJobStatus.ERROR]: PrintJobStatus.FAILED,
  [PrintNodePrintJobStatus.DONE]: PrintJobStatus.COMPLETED,
  [PrintNodePrintJobStatus.CANCELLED]: PrintJobStatus.CANCELLED,
  [PrintNodePrintJobStatus.DELETED]: PrintJobStatus.CANCELLED,
};

export const PrintStatusLabels: Record<PrintJobStatus, string> = {
  [PrintJobStatus.CREATED]: "Print job created",
  [PrintJobStatus.PURCHASED]: "Labels purchased",
  [PrintJobStatus.GENERATING]: "Generating PDF",
  [PrintJobStatus.GENERATED]: "PDF generated",
  [PrintJobStatus.INITIALIZING_PRINTING]: "Sending to print server",
  [PrintJobStatus.SENT_TO_PRINT_NODE]: "Sent to print server",
  [PrintJobStatus.PRINT_NODE_CONFIRMED]: "Print server confirmed",
  [PrintJobStatus.SENT_TO_PRINTER]: "Sent to printer",
  [PrintJobStatus.COMPLETED]: "Print job completed",
  [PrintJobStatus.BATCH_COMPLETED]: "Print job completed",
  [PrintJobStatus.FAILED]: "Print job failed",
  [PrintJobStatus.CANCELLING]: "Timing out print job",
  [PrintJobStatus.CANCELLED]: "Print job timed out",
};

// Substring embedded verbatim in the weather-unavailable message built by
// formatWeatherUnavailableMessage (redo/fulfillment/outbound-labels/db/util).
// The async print-queue UI only has the stored message string to work from, so
// isRetryablePrintJobError classifies the message as retryable by looking for
// this marker. Weather only ever blocks a print on a transient weather.gov
// outage (a resolvable US zip we couldn't fetch), which is retryable — a later
// print recovers. "Not enough data" cases (no/non-US destination) never block
// the print at all; they render an in-slip note instead. Keep this in sync with
// that formatter (its unit tests assert the message contains it).
export const WEATHER_SERVICE_OUTAGE_MARKER =
  "Weather data is temporarily unavailable";

const HumanReadablePrintJobErrors: [RegExp | string, string, boolean][] = [
  // PDF generation errors
  [/^generateDocumentPDF/, "Failed to generate PDF", true],
  ["No print node job id", "Failed to generate PDF", true],
  ["No url provided", "Failed to generate PDF", true],

  // Redo Connect crashes (various exit codes)
  [
    "worker exited with return code 3221226525",
    "Redo connect crashed, try restarting it",
    true,
  ],
  [
    "worker exited with return code 3221225477",
    "Redo connect crashed, try restarting it",
    true,
  ],
  [
    "worker exited with return code 3221226356",
    "Redo connect crashed, try restarting it",
    true,
  ],
  [
    "worker exited with return code 1073807364",
    "Redo connect crashed, try restarting it",
    true,
  ],
  [
    "worker exited with return code -15",
    "Print worker was terminated, try restarting Redo Connect",
    true,
  ],
  [
    "unhandled_exception; [WinError -529697949]",
    "Redo connect crashed, try restarting it",
    true,
  ],
  [
    "unhandled_exception; list indices must be integers, not str",
    "Redo Connect crashed, try restarting it",
    true,
  ],
  [
    "worker exited with return code 3221226505",
    "Print job failed. Please ensure that you are only running one instance of Redo Connect at a time.",
    false,
  ],
  [
    "worker exited with return code 3221225620",
    "Redo connect crashed, try restarting it",
    true,
  ],
  [
    "worker exited with return code job_timeout",
    "Print job timed out. Please try again.",
    true,
  ],

  // Printer engine errors
  [
    /engine4_error; engine4 pdfPrintStartJob\(\) return \d+\/invalid printer/,
    "Invalid printer. Please check your printer settings and ensure the correct printer is selected.",
    false,
  ],
  [
    "engine4_error; [WinError 3003] A StartDocPrinter call was not issued",
    "Printer failed to start the print document. Please try again or restart your printer.",
    true,
  ],

  // PDF errors
  [
    "The file is damaged and could not be repaired",
    "The PDF was corrupted and could not be printed. Please try reprinting.",
    true,
  ],

  // Printer configuration errors
  [
    /^printer '.*' not found on /,
    "Printer not found, check printer settings",
    false,
  ],
  [
    /^printer '.*' has been manually disabled$/,
    "Printer disabled, check printer settings",
    false,
  ],
  [
    "Win32 error 1801 from OpenPrinter()",
    "Invalid printer name, check printer settings",
    false,
  ],
  [
    "Win32 error 0 from DocumentProperties()",
    "Printer driver configuration error",
    false,
  ],

  // Printer service/connection errors
  [
    "Win32 error 1722 from OpenPrinter()",
    "Printer service unavailable, check printer connection",
    false,
  ],
  [
    "engine6_error; Unable to print document: An internal error occurred",
    "Printer internal error occurred",
    false,
  ],
  [
    "engine6_error; Unable to print document: Could not open the specified printer.",
    "Could not open the specified printer, check printer connection",
    false,
  ],
  [
    "engine6_error; Printer is unusable. Please reinstall driver.",
    "Printer is unusable. Please reinstall the printer driver",
    false,
  ],
  [
    "engine6_error; Unable to print document: Out of memory.",
    "Printer out of memory. Please restart Redo Connect",
    true,
  ],
  [
    "engine6_error; Win32 error 8 from DocumentProperties(). Not enough memory resources are available to process this command.",
    "Printer out of memory. Please restart Redo Connect",
    true,
  ],
  [
    "unhandled_exception; Unable to set current pm printer: -30880",
    "Printer connection issue. Your printer may be offline",
    false,
  ],

  // Redo Connect Python runtime error (list indexed with string key)
  [
    "unhandled_exception; list indices must be integers, not str",
    "Redo Connect crashed, try restarting it",
    true,
  ],

  // Weather forecast errors are handled by isRetryablePrintJobError directly
  // (see WEATHER_* markers below), not via this table: the messages built by
  // formatWeatherUnavailableMessage are already merchant-facing and name the
  // affected order(s)/zip, so we let them pass through unmodified rather than
  // replacing them with a generic label here.

  // Network / server response errors
  ["download failed", "Network error on printer", true],
  ["fetch failed", "Network error on printer", true],
  [
    "Unexpected token '<'",
    "Network error: received an unexpected response from the server. Please try again.",
    true,
  ],

  // Authorization errors
  [
    "Team does not have access to fulfillment group",
    "Your team doesn't have access to this order. Please contact Redo support.",
    false,
  ],

  // PrintNode API errors
  [
    /Idempotency key collision/,
    "Duplicate print job detected. Please try again.",
    true,
  ],

  // Label purchase errors
  ["Activity task timed out", "Failed to purchase labels", false],
  ["Activity task failed", "Unknown failure, try again", true],
  ["Label purchase failed", "Failed to purchase labels", true],

  // Temporal workflow describe failure (transient Temporal service error)
  [
    "Failed to describe workflow",
    "A temporary error occurred while processing your print job. Please try again.",
    true,
  ],

  [
    "activity ScheduleToClose timeout",
    "Transient printing error. Please try again.",
    true,
  ],
  ["HTTP 403", "Transient printing error. Please try again.", true],
  [
    "Gotenberg returned 503",
    "Transient printing error. Please try again.",
    true,
  ],
  [
    "Gotenberg returned 400",
    "Transient printing error. Please try again.",
    true,
  ],

  // Success status
  ["done", "Print job completed", false],
];

export function getHumanReadablePrintJobError(error?: string) {
  if (!error) {
    return;
  }

  for (const [regex, label] of HumanReadablePrintJobErrors) {
    if (regex instanceof RegExp && regex.test(error)) {
      return label;
    }
    if (typeof regex === "string" && error.includes(regex)) {
      return label;
    }
  }
  return error;
}

export function isRetryablePrintJobError(error?: string) {
  if (!error) {
    return false;
  }

  // Weather only blocks a print on a transient weather.gov outage, which is
  // retryable — a later print recovers. (Permanent "not enough data" cases
  // never reach here; they render an in-slip note instead of failing.)
  if (error.includes(WEATHER_SERVICE_OUTAGE_MARKER)) {
    return true;
  }

  for (const [regex, _, retryable] of HumanReadablePrintJobErrors) {
    if (regex instanceof RegExp && regex.test(error)) {
      return retryable;
    }
    if (typeof regex === "string" && error.includes(regex)) {
      return retryable;
    }
  }
  return false;
}

const RedoPrintJobEventSchema_ = z.object({
  timestamp: z.date(),
  status: createEnum(PrintJobStatus, { enumName: "PrintJobStatus" }),
  message: z.string().optional(),
});
export const RedoPrintJobEventSchema: z.ZodType<RedoPrintJobEvent> =
  RedoPrintJobEventSchema_;

export interface RedoPrintJobEvent
  extends z.infer<typeof RedoPrintJobEventSchema_> {}

export const RedoPrintJobBaseSchema = z.object({
  _id: zExt.objectId(),
  printNodeJobId: z.number().optional(),
  userId: z.string(),
  teamId: z.string(),
  url: z.string().optional(),
  documentType: createEnum(PrintableDocument, {
    enumName: "PrintableDocument",
  }),
  templateId: z.string().optional(),
  printerId: z.number().optional(),
  printerOptions: PrinterOptionsSchema.optional(),
  status: createEnum(PrintJobStatus, { enumName: "PrintJobStatus" }),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  printBatchCompletedAt: z.date().nullish(),
  dismissedAt: z.date().nullish(),
});

const RedoPrintJobDataSchema_ = z.union([
  z.object({ fulfillmentGroupIds: z.array(z.string()) }),
  z.object({ batchId: z.string() }),
]);
export const RedoPrintJobDataSchema: z.ZodType<RedoPrintJobData> =
  RedoPrintJobDataSchema_;
export type RedoPrintJobData = z.infer<typeof RedoPrintJobDataSchema_>;

const RedoPrintJobSchema_ = z.intersection(
  RedoPrintJobDataSchema_,
  z.object({
    ...RedoPrintJobBaseSchema.shape,
    events: z.array(RedoPrintJobEventSchema_),
  }),
);
export const RedoPrintJobSchema: z.ZodType<RedoPrintJob> = RedoPrintJobSchema_;

const RedoPrintJobOmitEventsSchema_ = z.intersection(
  RedoPrintJobDataSchema_,
  RedoPrintJobBaseSchema,
);
export const RedoPrintJobOmitEventsSchema: z.ZodType<RedoPrintJobOmitEvents> =
  RedoPrintJobOmitEventsSchema_;
export type RedoPrintJobOmitEvents = z.infer<
  typeof RedoPrintJobOmitEventsSchema_
>;

const CreateRedoPrintJobSchema = z.intersection(
  RedoPrintJobDataSchema_,
  z.object({
    ...RedoPrintJobBaseSchema.omit({ _id: true, status: true }).shape,
    _id: zExt.objectId().optional(),
  }),
);

export type CreateRedoPrintJob = z.infer<typeof CreateRedoPrintJobSchema>;

export type RedoPrintJob = z.infer<typeof RedoPrintJobSchema_>;
